import React, { useState } from 'react';
import patientInfoService from '../patientInfo/patientInfoService';
import AuthService from '../Security/AuthService';
import PointsBankAPI from '../points/PointsBankAPI';
import ReCAPTCHA from 'react-google-recaptcha';
import PatientContactPermissionService from '../patientInfo/PatientContactPermissionService';
import "../shared/PageWrapper.css";

const PatientRegistration = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    dob: '',
    age: '',
    phoneNumber: '',
    email: '',
    sex: '',
    notes: '',
    visits: 0,
    nfWc: 0,
    totalPoints: 0,
  });

  const [permissions, setPermissions] = useState({
    allowPromotionText: false,
    allowPromotionEmail: false,
    allowAppointmentReminderText: false,
    allowAppointmentReminderEmail: false,
    allowAuthorizationReminderText: false,
    allowAuthorizationReminderEmail: false,
  });

  const [captchaToken, setCaptchaToken] = useState(null);

  const patientService = new patientInfoService();
  const authSer = new AuthService();
  const pointsBank = new PointsBankAPI();

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      alert("Please verify you're not a robot.");
      return;
    }

    const calculatedAge = calculateAge(formData.dob);

    const registerRequest = {
      username: formData.username.trim(),
      password: formData.password.trim(),
      email: formData.email.trim(),
      captchaToken,
    };

    const detailsRequest = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      dateOfBirth: formData.dob,
      age: calculatedAge,
      visits: 0,
      nfWc: 0,
      sex: formData.sex.trim(),
      notes: formData.notes.trim(),
      phoneNumber: formData.phoneNumber.trim(),
      email: formData.email.trim(),
    };

    try {
      const registrationResponse = await patientService.registerCredentials(registerRequest);

      if (!registrationResponse.patientId) {
        throw new Error("Invalid patient ID returned from registration.");
      }

      const patientId = registrationResponse.patientId;

      const loginRequest = {
        username: "PatientAccount",
        password: "patientofnewu",
      };

      const loginResult = await authSer.login(loginRequest);
      const { token } = loginResult;

      const jwtExists = localStorage.getItem('jwt');
      if (!jwtExists || jwtExists === 'null' || jwtExists === 'undefined') {
        localStorage.setItem('jwt', token);
        localStorage.setItem('role', 'PATIENT');
        localStorage.setItem('username', formData.username);
        setTimeout(() => window.location.href = '/dashboard/patient', 1500);
      }

      await patientService.savePatientDetails(patientId, formData.username, detailsRequest, token);

      // Points setup: create entry and award 5 welcome points
      const entry = {
        patientId,
        totalPoints: 0
      };
      await pointsBank.addPointsEntry(entry);
      await pointsBank.addFromAppointment(patientId, 5);

      const contactService = new PatientContactPermissionService();
      const contactPermissions = {
        patientId,
        email: formData.email,
        phone: formData.phoneNumber,
        ...permissions,
      };
      await contactService.add(contactPermissions);

      alert("Registration successful. You've earned 5 welcome points!");

    } catch (error) {
      console.error("Error in registration:", error.message);
      if (error.message.includes("Email is already registered")) {
        alert("That email is already in use. Please use a different one.");
      } else if (error.message.includes("Username already exists")) {
        alert("That username is already taken. Please choose another.");
      } else {
        alert("Error: " + error.message);
      }
    }
  };

  return (
    <div className="pageWrapper">
      <div className="formContainer">
        <form id="registrationForm" onSubmit={handleSubmit}>
          {[
            { type: 'text', id: 'username', placeholder: 'Enter Username', label: 'Your unique username.' },
            { type: 'password', id: 'password', placeholder: 'Enter Password', label: 'Use at least 8 characters.' },
            { type: 'text', id: 'firstName', placeholder: 'Enter First Name', label: 'Your first name.' },
            { type: 'text', id: 'lastName', placeholder: 'Enter Last Name', label: 'Your last name.' },
            { type: 'date', id: 'dob', placeholder: 'Enter Date of Birth', label: 'Format: YYYY-MM-DD' },
            { type: 'tel', id: 'phoneNumber', placeholder: 'Enter Phone Number', label: 'Provide a valid phone number.' },
            { type: 'email', id: 'email', placeholder: 'Enter Email', label: 'Provide a valid email.' },
          ].map(({ type, id, placeholder, label }) => (
            <div className="input-container" key={id}>
              <input
                type={type}
                id={id}
                placeholder={placeholder}
                value={formData[id]}
                onChange={handleChange}
                required
              />
              <span className="tooltip">{label}</span>
            </div>
          ))}

          <div className="input-container">
            <select id="sex" value={formData.sex} onChange={handleChange} required>
              <option value="" disabled>Select Sex</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            <span className="tooltip">Choose gender identity.</span>
          </div>

          <div className="input-container">
            <textarea
              id="notes"
              placeholder="Enter Notes (Optional)"
              rows={3}
              value={formData.notes}
              onChange={handleChange}
            />
            <span className="tooltip">Additional information about the patient.</span>
          </div>

          <div className="checkbox-group">
            <label><strong>Contact Preferences:</strong></label>
            {[
              {
                title: "Promotions/Messages",
                ids: ["allowPromotionText", "allowPromotionEmail"],
                description: `By checking these boxes, you agree to receive promotional and messages from New 'U' Physical Therapy via SMS or email. These will include all promotions and message details to let you be notified and understand what it is. 
                Your consent does is not a condition of promotion purchases. You can end these messages anytime by going into your dashboard.`
              },
              {
                title: "Appointment Reminders",
                ids: ["allowAppointmentReminderText", "allowAppointmentReminderEmail"],
                description: `By checking these boxes, you agree to receive appointment reminders via SMS or email. These reminders will be sent on the same day to remind you about appointments. You can cancel anytime in the dashboard.`
              },
              {
                title: "Authorization Notifications",
                ids: ["allowAuthorizationReminderText", "allowAuthorizationReminderEmail"],
                description: `By checking these boxes, you agree to receive updates about your insurance authorizations via text or email. If your insurance requires for you to have authorization for visits this will sent you reminders one month and 2 weeks before expiration if you still have visits avaiable, as well as if your visits run out. You can cancel anytime in the dashboard.`
              },
            ].map(({ title, ids, description }) => (
              <div key={title} className="checkbox-row">
                {ids.map((id) => (
                  <div key={id} className="checkbox-column">
                    <div className="checkbox-container">
                      <input
                        type="checkbox"
                        id={id}
                        checked={permissions[id]}
                        onChange={(e) =>
                          setPermissions(prev => ({ ...prev, [id]: e.target.checked }))
                        }
                      />
                      <label htmlFor={id}>
                        {id.includes("Text") ? `${title} via text` : `${title} via email`}
                      </label>
                    </div>
                  </div>
                ))}
                <div className="consent-description">
                  <p>{description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="input-container">
            <ReCAPTCHA
              sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
              onChange={(token) => setCaptchaToken(token)}
            />
            <span className="tooltip">Confirm you are not a robot.</span>
          </div>

          <button type="submit">Register</button>
        </form>
      </div>
    </div>
  );
};

export default PatientRegistration;
